import type { PageColumn } from "@artis/sdk";
import { defaultFieldNameSelector, type TData } from "@package/grid-utils";
import { useMemo, useState, useEffect } from "react";
import type { GridApi } from "ag-grid-community";
import { nanoid } from "nanoid";

import { Autocomplete, type TOption } from "../../components/Autocomplete/Autocomplete";
import { useAutocomplete } from "../../components/Autocomplete/hooks";
import { FilterToggleButton } from "../../components/FilterToggleButton";
import { colParams, compareIdx } from "../../utils";
import { columnWidth as fallbackColumnWidth } from "../../globals";
import { useGrid } from "../stores";
import {
  computeColumnInsertIdxs,
  effectiveColumnInsertPosition,
  useDefaultCurveColumnWidth,
  useInsertColumnPosition,
  useActivePageId,
  useInsertPageProducts,
  usePageProducts,
  useProductsByIds,
} from "../../../data";
import type { InsertPosition } from "../../../data";
import type { PageProductsFragmentFragment } from "../../../__generated__/gql/graphql";
import { Radio, RadioGroup } from "@artis/ui";
import { Button } from "../../ui";
import { useAllProductsOptions } from "../../curve-management/hooks";
import {
  CurveFilter,
  CurveFilterPreview,
  CurveFilterProvider,
  type TCurveFilter,
} from "../../curve-management/CurveFilter";
import { CloseIcon } from "../../icons/CloseIcon";
import { ModalActionsRow } from "../../components/ModalActionsRow";
import { useUserSubscriptionTier } from "../../../data/user";
import { useUserId } from "../../../context/auth/hooks";
import { PRA_MOCK_PRODUCTS } from "../../../data/pra/praMock";

interface HandleAddProductsToPageParams {
  api?: GridApi<TData> | null;
  userId: string;
  pageId: string;
  currentPageProducts: PageProductsFragmentFragment[];
  selectedValues: TOption[];
  curveWidth?: number;
}

const praProductOptions = (): TOption[] =>
  PRA_MOCK_PRODUCTS.map((product) => ({
    label: product.name,
    value: product.id,
    groupValue: product.package ?? "PRA",
  }));

function getPageProducts({
  api,
  userId,
  pageId,
  curveWidth,
  currentPageProducts,
  selectedValues,
  insertColumnPosition,
  pra,
}: HandleAddProductsToPageParams & {
  insertColumnPosition: InsertPosition;
  pra?: boolean;
}) {
  const sorted = currentPageProducts.sort(compareIdx);
  const focusedParams = colParams(api?.getFocusedCell()?.column);
  const position = effectiveColumnInsertPosition(insertColumnPosition, focusedParams?.entityId === "main-month");
  const currentlyFocusedId = focusedParams?.gridId;
  const idxs = computeColumnInsertIdxs(sorted, selectedValues.length, position, currentlyFocusedId);

  return selectedValues.map((product, index) => {
    const artis_type = product.metadata?.artis_type;
    const gridId = nanoid();

    return {
      id: gridId,
      grid_id: gridId,
      idx: idxs[index],
      product_id: product.value,
      user_id: userId,
      page_id: pageId,
      column_type: "product",
      column_width: curveWidth ?? fallbackColumnWidth,
      column_field_selector: artis_type
        ? defaultFieldNameSelector(artis_type)
        : pra
          ? PRA_MOCK_PRODUCTS.find((item) => item.id === product.value)?.defaultField
          : undefined,
      decimal_places: undefined,
      thousands_separator: undefined,
    } satisfies PageProductsFragmentFragment;
  });
}

const toPageColumn = (product: PageProductsFragmentFragment): PageColumn | undefined => {
  if (!product.page_id || !product.product_id || product.idx == null) return undefined;
  return {
    id: product.id,
    pageId: product.page_id,
    productId: product.product_id,
    field: product.column_field_selector ?? "",
    order: product.idx,
    ...(product.column_width != null ? { width: product.column_width } : {}),
  };
};

export default function InsertCurveModal({
  curveWidth,
  onCancel,
  onDone,
  supportMessage = "This Page has no Curves. Please contact Support.",
  pra = false,
  insertColumns,
}: {
  curveWidth?: number;
  onCancel?: () => void;
  onDone?: () => void;
  /** Shown instead of the add form for a user who cannot add products. */
  supportMessage?: string;
  /** List the PRA mock series instead of market curves. */
  pra?: boolean;
  /** PRA inserts through the pages API, which shows the column before the server write returns. */
  insertColumns?: (columns: readonly PageColumn[]) => void;
}) {
  const [filter, setFilter] = useState<"live" | "snapshot" | "delta" | "all">("live");
  const pageId = useActivePageId();
  const userId = useUserId();
  const [api] = useGrid();

  const [listHeight, setListHeight] = useState(200);

  useEffect(() => {
    const calculateListHeight = () => {
      const modalOverhead = 450;
      const bottomMargin = 20;
      const availableHeight = window.innerHeight - modalOverhead - bottomMargin;
      const boundedHeight = Math.max(150, Math.min(availableHeight, 400));
      setListHeight(boundedHeight);
    };

    calculateListHeight();
    window.addEventListener("resize", calculateListHeight);

    return () => {
      window.removeEventListener("resize", calculateListHeight);
    };
  }, []);

  const { liteUser } = useUserSubscriptionTier();
  const products = usePageProducts(useActivePageId());
  const insert = useInsertPageProducts();
  const insertColumnPosition = useInsertColumnPosition().data ?? "right_of_current";
  const defaultCurveColumnWidth = useDefaultCurveColumnWidth().data ?? fallbackColumnWidth;

  const productIds = (products.data ?? []).map((product) => product.product_id).filter(Boolean);

  const dbProducts = useProductsByIds(productIds);

  const disabledIds = (dbProducts.data ?? []).filter((x) => x?.artis_type === "customer_curve").map((x) => x.id);

  const { data: productOptionsData } = useAllProductsOptions({
    disabledIds,
    filter: [filter],
    disableListPermission: true,
  });
  const {
    types: productOptionsTypes,
    regions: productOptionsRegions,
    commodities: productOptionsCommodities,
    curves: productOptionsCurves,
  } = productOptionsData;

  const useAutocompleteProps = useAutocomplete({ multiple: true });
  const {
    multipleState: [selectedValues],
  } = useAutocompleteProps;

  const [filters, setFilters] = useState<TCurveFilter>({
    types: [],
    regions: [],
    commodities: [],
  });

  const { types, regions, commodities } = filters;

  const isFiltered = useMemo(() => {
    if (types.length === 0 && regions.length === 0 && commodities.length === 0) {
      return false;
    }

    return (
      types.length !== productOptionsTypes.length ||
      regions.length !== productOptionsRegions.length ||
      commodities.length !== productOptionsCommodities.length
    );
  }, [types, regions, commodities, productOptionsTypes, productOptionsRegions, productOptionsCommodities]);

  const filteredProducts = useMemo(() => {
    return (
      productOptionsCurves.filter((curve) => {
        const type = curve.metadata?.product_type;
        const region = curve.metadata?.geographicalRegionByGeographicalRegion?.geographical_region;
        const commodity = curve.metadata?.commodityGroupByCommodityGroup?.commodity_group;

        return (
          (types.length === 0 || !type || types.includes(type)) &&
          (regions.length === 0 || !region || regions.includes(region)) &&
          (commodities.length === 0 || !commodity || commodities.includes(commodity))
        );
      }) || []
    );
  }, [types, regions, commodities, productOptionsCurves]);

  const handleSave = () => {
    const pageProducts = getPageProducts({
      api,
      userId,
      pageId,
      currentPageProducts: products.data ?? [],
      selectedValues,
      curveWidth: curveWidth ?? defaultCurveColumnWidth,
      insertColumnPosition,
      pra,
    });

    for (const p of pageProducts) {
      for (const product of products.data ?? []) {
        if (product.idx === p.idx) {
          // This should never happen, and if it does, it'll cause other issues like columns being inserted in the wrong place.
          console.error("Column already exists at this index");
          return;
        }
      }
    }

    if (insertColumns) {
      const columns = pageProducts.flatMap((product) => {
        const column = toPageColumn(product);
        return column ? [column] : [];
      });
      insertColumns(columns);
    } else {
      insert.mutate(pageProducts);
    }
    onDone?.();
  };

  return liteUser ? (
    <div className="text-foreground">{supportMessage}</div>
  ) : (
    <div className="flex flex-col gap-[var(--padding-medium-500,15px)] w-full">
      <div className="flex items-center justify-between">
        <div className="text-[13.5px] font-semibold leading-[21px] text-foreground">Add to current Page</div>
        {onCancel && <CloseIcon onClick={onCancel} />}
      </div>

      {pra ? null : (
        <RadioGroup>
          <Radio label="Live" checked={filter === "live"} onChange={() => setFilter("live")} />
          <Radio label="Snapshot" checked={filter === "snapshot"} onChange={() => setFilter("snapshot")} />
          <Radio label="Delta" checked={filter === "delta"} onChange={() => setFilter("delta")} />
          <Radio label="All" checked={filter === "all"} onChange={() => setFilter("all")} />
        </RadioGroup>
      )}

      <div className="flex flex-col gap-[var(--padding-small-100,3px)]">
        <p className="text-foreground text-[12px] leading-[18px]">{pra ? "Select Product(s)" : "Select Curve(s)"}</p>
        <CurveFilterProvider>
          <div className="flex flex-col gap-2">
            <div className="flex gap-[var(--spacing-scale-small-200,6px)] items-center">
              <div data-testid="product-select" className="flex-1 min-w-0">
                <Autocomplete
                  multiple
                  {...useAutocompleteProps}
                  autoComplete={true}
                  autoFocus={true}
                  options={pra ? praProductOptions() : filteredProducts || []}
                  listHeight={listHeight}
                  placeholder={pra ? "Search Products…" : "Search Curves…"}
                />
              </div>
              {!pra && productOptionsData && (
                <CurveFilter products={productOptionsData} filters={filters} setFilters={setFilters}>
                  <FilterToggleButton isActive={isFiltered} />
                </CurveFilter>
              )}
            </div>
            <CurveFilterPreview filters={filters} setFilters={setFilters} />
          </div>
        </CurveFilterProvider>
      </div>
      <ModalActionsRow className="mt-[12px]">
        {api && onCancel && (
          <Button buttonType="cancel" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          data-testid="add-curves-to-page-button"
          disabled={!userId || insert.isPending || !selectedValues.length}
          buttonType="save"
          onClick={handleSave}
        >
          Add
        </Button>
      </ModalActionsRow>
    </div>
  );
}
